
///
/// EarthBrowser Design System - Canvas Styles
///
/// Reusable canvas styling methods for consistent UI rendering
/// Reference: documentation/DESIGN_SYSTEM.md
///

import { type Canvas } from "@stovokor/uipanes/Canvas"
import { rgba, type color } from "@stovokor/math/color"
import { DesignColors } from "./DesignColors"
import { DesignSpacing } from "./DesignSpacing"

export type ThemeType = 'satellite' | 'location' | 'volcano' | 'quake' | 'aircraft' | 'ship' | 'groundstation' | 'buoy' | 'default'

export interface GlassmorphismOptions {
	radius?: number
	backdropAlpha?: number
	borderAlpha?: number
	borderWidth?: number
	withGlow?: boolean
	glowBlur?: number
}

export interface ModernDarkPanelOptions {
	radius?: number
	shadowBlur?: number
	borderWidth?: number
}

export class CanvasStyles {
	/**
	 * Renders glassmorphism background with theme gradient
	 *
	 * This is the standard style for hover info panels and transient overlays.
	 * Uses dark backdrop, subtle gradient, and glass border.
	 *
	 * @param canvas - The canvas to render on
	 * @param theme - The theme type (determines gradient colors)
	 * @param options - Optional styling parameters
	 */
	static renderGlassmorphism(
		canvas: Canvas,
		theme: ThemeType = 'default',
		options?: GlassmorphismOptions
	): void {
		const radius = options?.radius ?? DesignSpacing.RADIUS_XL
		const backdropAlpha = options?.backdropAlpha ?? 0.4
		const borderAlpha = options?.borderAlpha ?? 0.2
		const borderWidth = options?.borderWidth ?? 1
		const withGlow = options?.withGlow ?? false
		const glowBlur = options?.glowBlur ?? 12

		// Dark backdrop for glassmorphism effect
		canvas.rectangle(0, 0, canvas.width, canvas.height, radius)
			.fill(rgba(0, 0, 0, backdropAlpha))

		// Theme gradient overlay
		const gradient = this.getThemeGradient(canvas.context, theme, canvas.width, canvas.height)
		canvas.context.fillStyle = gradient
		canvas.context.fillRect(0, 0, canvas.width, canvas.height)

		// Optional glow effect
		if (withGlow) {
			canvas.shadow(rgba(37/255, 99/255, 235/255, 0.4), glowBlur, 0, 0)
		}

		// Border with glass effect
		canvas.rectangle(0, 0, canvas.width, canvas.height, radius)
			.stroke(rgba(1, 1, 1, borderAlpha), borderWidth)

		// Reset shadow
		if (withGlow) {
			canvas.shadow(rgba(0, 0, 0, 0), 0, 0, 0)
		}
	}

	/**
	 * Renders modern dark HUD panel background
	 *
	 * This is the style for prominent information windows and focused entity details.
	 * Uses darker background with shadow for depth.
	 *
	 * @param canvas - The canvas to render on
	 * @param options - Optional styling parameters
	 */
	static renderModernDarkPanel(
		canvas: Canvas,
		options?: ModernDarkPanelOptions
	): void {
		const radius = options?.radius ?? DesignSpacing.RADIUS_XL
		const shadowBlur = options?.shadowBlur ?? 16
		const borderWidth = options?.borderWidth ?? 2

		// Modern dark background with shadow
		canvas.rectangle(0, 0, canvas.width, canvas.height, radius)
			.shadow(DesignColors.PANEL_DARK_SHADOW, 0, 4, shadowBlur)
			.fill(DesignColors.PANEL_DARK_BG)

		// Prominent border
		canvas.rectangle(0, 0, canvas.width, canvas.height, radius)
			.stroke(DesignColors.PANEL_DARK_BORDER, borderWidth)
	}

	/**
	 * Renders a connector line from point to panel
	 *
	 * Draws a dog-leg connector line with consistent styling
	 *
	 * @param canvas - The canvas to render on
	 * @param fromX - Source X coordinate
	 * @param fromY - Source Y coordinate
	 * @param toX - Panel X coordinate
	 * @param toY - Panel Y coordinate
	 * @param panelWidth - Width of the target panel
	 * @param panelHeight - Height of the target panel
	 * @param options - Optional styling (lineWidth, color, withGlow)
	 */
	static renderConnectorLine(
		canvas: Canvas,
		fromX: number,
		fromY: number,
		toX: number,
		toY: number,
		panelWidth: number,
		panelHeight: number,
		options?: { lineWidth?: number, color?: string | color, withGlow?: boolean }
	): void {
		// Only draw if we have valid coordinates
		if (fromX < 0 || fromY < 0) {
			return
		}

		const lineWidth = options?.lineWidth ?? 1.5
		const withGlow = options?.withGlow ?? false
		const connectorColor = options?.color ?? DesignColors.CONNECTOR

		// Calculate panel center point for connection
		const panelCenterX = toX + panelWidth / 2
		const panelCenterY = toY + panelHeight / 2

		// Determine which side of the panel to connect from
		const isPanelRight = toX > fromX
		const isPanelBelow = toY > fromY
		const isPanelAbove = (toY + panelHeight) < fromY

		canvas.beginPath()

		if (isPanelRight) {
			// Panel is to the right - horizontal connector
			const panelLeftX = toX
			const horizontalDistance = DesignSpacing.CONNECTOR_OFFSET

			canvas.moveTo(fromX, fromY)
				.lineTo(fromX + horizontalDistance, fromY)
				.lineTo(panelLeftX, panelCenterY)
		} else if (isPanelBelow) {
			// Panel is below - vertical connector
			const panelTopY = toY
			const verticalDistance = DesignSpacing.CONNECTOR_OFFSET

			canvas.moveTo(fromX, fromY)
				.lineTo(fromX, fromY + verticalDistance)
				.lineTo(panelCenterX, panelTopY)
		} else if (isPanelAbove) {
			// Panel is above - vertical connector
			const panelBottomY = toY + panelHeight
			const verticalDistance = DesignSpacing.CONNECTOR_OFFSET

			canvas.moveTo(fromX, fromY)
				.lineTo(fromX, fromY - verticalDistance)
				.lineTo(panelCenterX, panelBottomY)
		} else {
			// Panel is to the left - horizontal connector
			const panelRightX = toX + panelWidth
			const horizontalDistance = DesignSpacing.CONNECTOR_OFFSET

			canvas.moveTo(fromX, fromY)
				.lineTo(fromX - horizontalDistance, fromY)
				.lineTo(panelRightX, panelCenterY)
		}

		// Optional glow effect
		if (withGlow) {
			canvas.shadow(DesignColors.CONNECTOR_GLOW, 8, 0, 0)
		}

		// Draw the connector
		if (typeof connectorColor === 'string') {
			// Parse string color or use directly
			canvas.context.strokeStyle = connectorColor
			canvas.context.lineWidth = lineWidth
			canvas.context.stroke()
		} else {
			canvas.stroke(connectorColor, lineWidth)
		}

		// Reset shadow
		if (withGlow) {
			canvas.shadow(rgba(0, 0, 0, 0), 0, 0, 0)
		}
	}

	/**
	 * Gets the appropriate gradient for a theme
	 */
	private static getThemeGradient(
		context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
		theme: ThemeType,
		width: number,
		height: number
	): CanvasGradient {
		switch (theme) {
			case 'satellite':
				return DesignColors.createSatelliteGradient(context, width, height)
			case 'location':
				return DesignColors.createLocationGradient(context, width, height)
			case 'volcano':
				return DesignColors.createVolcanoGradient(context, width, height)
			case 'quake':
				return DesignColors.createQuakeGradient(context, width, height)
			case 'aircraft':
				return DesignColors.createAircraftGradient(context, width, height)
			case 'ship':
				return DesignColors.createShipGradient(context, width, height)
			case 'groundstation':
				return DesignColors.createGroundStationGradient(context, width, height)
			case 'buoy':
				return DesignColors.createBuoyGradient(context, width, height)
			default:
				return DesignColors.createDefaultGradient(context, width, height)
		}
	}
}
